<script setup lang="ts">
import { CONFIRM_MODERATION_REJECT_POST } from '@@/bits/confirmation_dialog'
import { __ } from '@@/bits/intl'
import { postBannerBackgroundColorTailwindClass } from '@@/bits/post_color'
import { postModerationWarningText } from '@@/bits/post_moderation'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Post } from '@@/types'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const props = defineProps<{
  post: Post
  colorScheme: 'light' | 'dark'
}>()

const surfaceStore = useSurfaceStore()
const { isFrozen } = storeToRefs(surfaceStore)
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { openConfirmationDialog } = useGlobalConfirmationDialogStore()
const { approvePost } = useSurfacePostActionStore()
const { rejectPost } = useSurfacePostsStore()
const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())

// #region UI for moderators
function confirmModerationReject(): void {
  openConfirmationDialog({
    ...CONFIRM_MODERATION_REJECT_POST,
    afterConfirmActions: [() => rejectPost(props.post.id, props.post.hashid)],
    xShadow: true,
  })
}

const xContentModerationWarning = computed<boolean>(() => {
  return canIModerate.value && !!props.post.moderation_violations && props.post.moderation_violations.length > 0
})

const warningText = computed<string>(() => {
  return postModerationWarningText({
    moderationViolations: props.post.moderation_violations,
    useLongText: false,
  })
})
// #endregion

// #region "pending approval" UI for post author
const isPostApproved = computed((): boolean => {
  return Boolean(props.post.published)
})

const isCurrentUserPostAuthor = computed((): boolean => {
  return props.post.author_id === currentUser.value.id
})

const isAwaitingApproval = computed((): boolean => {
  return isCurrentUserPostAuthor.value && !isPostApproved.value && !canIModerate.value
})
// #endregion

const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const { getDisplayedColorForPost } = surfacePostPropertiesStore

const postBannerBgColorClass = computed(() => {
  const postColor = getDisplayedColorForPost(props.post)
  return postBannerBackgroundColorTailwindClass({
    postColor,
    isLightColorScheme: props.colorScheme === 'light',
  })
})
</script>

<template>
  <div
    :class="[
      'post-moderation-banner relative',
      'h-10',
      'flex items-center',
      'rounded-es-xl rounded-ee-xl',
      postBannerBgColorClass,
    ]"
  >
    <div :class="['flex flex-row items-center gap-x-4', 'ps-2']">
      <template v-if="isAwaitingApproval">
        <span class="italic">{{ __('Awaiting approval') }}</span>
      </template>
      <template v-else>
        <div v-if="xContentModerationWarning" class="flex items-center gap-x-1.5">
          <OzIcon name="warning_filled" :size="16" class="text-danger-100" />
          <span
            class="text-body-extra-small"
            :class="[colorScheme === 'dark' ? 'text-light-text-200' : 'text-dark-text-200']"
          >
            {{ warningText }}
          </span>
        </div>
        <div :class="['flex items-center gap-x-1.5']">
          <OzContainedButton
            :size-preset="OzContainedButtonSizePreset.H24px"
            :dark-mode="colorScheme === 'dark'"
            :text="__('Approve')"
            :disabled="isFrozen"
            data-testid="surfacePostModerationApproveButton"
            @click.stop="approvePost({ postCid: post.cid })"
          />
          <OzContainedButton
            :size-preset="OzContainedButtonSizePreset.H24px"
            :color-scheme="OzContainedButtonColorScheme.Secondary"
            :dark-mode="colorScheme === 'dark'"
            :text="__('Reject')"
            :disabled="isFrozen"
            data-testid="surfacePostModerationRejectButton"
            @click.stop="confirmModerationReject"
          />
        </div>
      </template>
    </div>
    <div :class="['absolute inset-x-0 h-2.5 -top-2.5', postBannerBgColorClass]" />
  </div>
</template>
